import { Component, OnInit, OnDestroy, Input, AfterViewInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { UntypedFormGroup, UntypedFormBuilder, Validators, UntypedFormArray } from '@angular/forms';
import { DateAdapter } from '@angular/material/core';

import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject } from 'rxjs';
import moment from 'moment';

import { PracticeModel } from '../../../core/models/practice.model';
import { AppointmentModel } from 'src/app/core/models/appointment-model';
import { Settings } from 'src/app/app.settings.model';
import { AppSettings } from 'src/app/app.settings';
import { StandbyService } from '../standby.service';
import { CustomerSearchResult } from 'src/app/core/models/customer-search-result';
import { SearchCustomerService } from 'src/app/core/services/search-customer.service';
import { AuthenticateService } from 'src/app/core/services/authenticate.service';
import { SelectListItem } from 'src/app/core/models/select-list-item';
import { timeCheckValidator } from 'src/app/validators/app-validators';
import { Utils } from 'src/app/core/utils';
import { StandByModel } from 'src/app/core/interfaces/standby-model';
import { TerminologyItem } from 'src/app/core/interfaces/terminology-item';
import { ComplexBranch } from 'src/app/core/interfaces/complex-branch';
import { BaseEntity } from 'src/app/core/interfaces/base-entity';
import { SearchCustomerFilter } from 'src/app/core/interfaces/search-customer-filter';
import { StandByFormValues } from 'src/app/core/interfaces/standby-form-values';
import { StandByModelToSend } from 'src/app/core/interfaces/standby-model-tosend';
import { ScopeService } from 'src/app/core/services/scope.service';
import { FindTimeService } from 'src/app/core/services/find-time.service';
import { Patient } from 'src/app/core/interfaces/patient';
import { AppData } from 'src/app/app.data';
import { PriceListCode } from 'src/app/core/models/price-list-code';
import { TreatmentEntity } from 'src/app/core/interfaces/treatment-entity';


@Component({
  selector: 'app-standby-details',
  templateUrl: './standby-details.component.html',
  styleUrls: ['./standby-details.component.scss']
})
export class StandbyDetailsComponent implements OnInit, OnDestroy, AfterViewInit {

  @Input() selectedItem: CustomerSearchResult;
  results: CustomerSearchResult[];
  loading: boolean;
  message = '';


  private model: StandByModel;
  private hasPermission: boolean;
  private searchText = new Subject<string>();
  private patients: Observable<CustomerSearchResult[]>;

  public schedulers: SelectListItem[] = [];
  public rooms: SelectListItem[] = [];
  public practices: PracticeModel[] = [];
  public terminology: TerminologyItem[] = [];
  public branches: ComplexBranch[] = [];
  // public visitReasons: VisitReason[] = [];

  public treatments: TreatmentEntity[] = []; // if treatments from patient card
  public priceListCodes: PriceListCode[] = []; // if treatments from pricelist

  public appointments: AppointmentModel[] = [];
  public firstDayOfWeek: number = 0;
  public form: UntypedFormGroup;
  public settings: Settings;
  public error: string;
  public atLeastOneReqDropDownSelected: boolean; // used to enable\disable the submit button

  private subPractice: any;
  private subBranch: any;
  private subScheduler: any;
  private subRoom: any;
  

  sub: any;
  eventID: any;
  waitlistItemID: number;
  fromTimeList: SelectListItem[] = [];
  toTimeList: SelectListItem[] = [];
  minDuration: number = 5;
  maxDuration: number = 720; // 12 hours
  priorityList: number[] = [1, 2, 3, 4];
  minTime:string;
  maxTime:string;

  constructor(
    public appSettings: AppSettings,
    public translate: TranslateService,
    public fb: UntypedFormBuilder,
    private adapter: DateAdapter<any>,
    private standbyService: StandbyService,
    public router: Router,
    private route: ActivatedRoute,
    private searchService: SearchCustomerService,
    private scopeService: ScopeService,
    private authService: AuthenticateService,
    private findtimeService: FindTimeService,
    private appData: AppData) {

    this.settings = appSettings.settings;
    // moment.locale(this.settings.locale);
    this.hasPermission = authService.claims.CanEditConfidentialPatients || false;
  }

  ngOnInit() {
    this.getScopeFromSession();
    this.initForm();
    this.fillFormByEventID();
    this.fillFormByFindTime();
    this.fillFormByWaitlistItem(); // edit waitlistItem
    

    // load items must be after the form initialized
    this.getBranches(this.f.practice.value);
    this.getScheduler(this.f.practice.value, this.f.branch.value);
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
  }

  ngAfterViewInit() {
    this.settings.loadingSpinner = false;
  }

  fillFormByEventID() {
    this.sub = this.route.params.subscribe(params => {
      this.eventID = +params['eventID']; // (+) converts string 'id' to anumber
      if (this.eventID > 0) {
        this.getWaitListItemByEventID(this.eventID);
      }
    });
  }

  fillFormByWaitlistItem() {
    this.sub = this.route.params.subscribe(params => {
      this.waitlistItemID = +params['waitlistItemID']; // (+) converts string 'id' to a number
      if (this.waitlistItemID > 0) {
        this.getWaitListItemByWaitlistItemID(this.waitlistItemID);
      }
    });
  }

  fillFormByFindTime() {
    const values = this.appData.formStateFindTime;
    if (values) {

      this.f.waitlistID.setValue(null);

      if (values.patient) {
        const customer = values.patient as CustomerSearchResult;
        const patient: Patient = { ID: customer.EntityID, Name: customer.Name, Email: customer.Email };
        this.f.patient.setValue(patient);
      }
      if (values.practice) {
        this.f.practice.setValue(+values.practice); // the (+) sign convert the string to number
      }
      if (values.branch) {
        this.f.branch.setValue(+values.branch);
      }
      if (values.scheduler) {
        this.f.scheduler.setValue(values.scheduler);
      }

      if (values.reason) {
        this.f.reason.setValue(values.reason);
        this.treatments = this.appData.treatments;
        this.priceListCodes = this.appData.priceListCodes;
      }

      this.f.sunday.setValue(values.sunday || false);
      this.f.monday.setValue(values.monday || false);
      this.f.tuesday.setValue(values.tuesday || false);
      this.f.wednesday.setValue(values.wednesday || false);
      this.f.thursday.setValue(values.thursday || false);
      this.f.friday.setValue(values.friday || false);
      this.f.saturday.setValue(values.saturday || false);
      this.f.duration.setValue(values.duration);


      const startTime = values.startTime || '08:00';
      const endTime = values.endTime || '18:00';

      this.f.startTime.setValue(startTime);
      this.f.endTime.setValue(endTime);

    
      if (startTime && endTime) {
        this.fromTimeList = Utils.generateTimeList('08:00', '20:30', 30, false);
        this.toTimeList = Utils.generateTimeList('08:30', '21:00', 30, true);
      }
    }
  }


  /** load scheduler, practices, firstDay of week */
  getScopeFromSession() {
    const scope = this.scopeService.userScope;
    if (scope) {
      // this.schedulers = scope.schedulers;
      this.practices = scope.practices;
      this.firstDayOfWeek = scope.firstDayOfWeek;
      this.terminology = scope.terminology;
      this.minTime = scope.minTime;
      this.maxTime = scope.maxTime;
    }
  }

  getWaitListItemByEventID(eventID) {
    this.standbyService.getWaitListByEventID(eventID).subscribe(result => {
      if (result.Code === 1) {
        // success
        this.model = result.Data as StandByModel;
        this.fillForm(this.model);
      } else {
        this.error = this.translate.instant(`ERROR.CODE.${result.Code}`);
      }
    }, error => {
      this.error = error;
    });
  }

  getWaitListItemByWaitlistItemID(waitlistItemID: number) {
    this.standbyService.GetModelByWaitListItemID(waitlistItemID).subscribe(result => {
      if (result.Code === 1) {
        // success
        this.model = result.Data as StandByModel;
        this.fillForm(this.model);
      } else {
        this.error = this.translate.instant(`ERROR.CODE.${result.Code}`);
      }
    }, error => {
      this.error = error;
    });
  }

  initForm() {

    const person: BaseEntity = {
      ID: 0,
      Name: ''
    };

    // if called from appointent menu
    const appointment = this.appData.appointment;
    const waitlistItem = this.appData.waitlistItem;
    let eventID = 0;

    if (appointment) {
      person.ID = +appointment.EntityID; // the (+) sign convert string to number
      person.Name = appointment.Title;
      eventID = appointment.EventID;
    }

    if (waitlistItem) {
      person.Name = waitlistItem.CustomerName;
      person.ID = waitlistItem.CustomerID;
    }


    this.form = this.fb.group({
      'waitlistID': [0],
      'patient': [person, Validators.required],
      'practice': [null, Validators.required],
      'branch': [null, Validators.required],
      'scheduler': [null, Validators.required],
      'room': [null],
      'reason': [null, Validators.required],
      'reasonText': '',
      'note': [null],
      'priority': [4],
      'startDate': [moment(), Validators.required],
      'endDate': [moment().add(1, 'M'), Validators.required], // add 1 month
      'startTime': [this.minTime, Validators.required],
      'endTime': [this.maxTime, Validators.required],
      'duration': [30, Validators.compose([Validators.required, Validators.min(this.minDuration), Validators.max(this.maxDuration)])],
      'sunday': [true],
      'monday': [true],
      'tuesday': [true],
      'wednesday': [true],
      'thursday': [true],
      'friday': [true],
      'saturday': [true],
      'eventID': [eventID],
      'active': [true]
    },
      { validator: timeCheckValidator('startTime', 'endTime') });

    this.onChange();
  }

  fillForm(model: StandByModel) {
    if (model) {
      this.f.waitlistID.setValue(model.SchedulerWaitListID);

      if (model.UserPractiseID) {
        this.f.practice.setValue(+model.UserPractiseID); // the (+) sign convert the string to number
      }
      if (model.BranchID) {
        this.f.branch.setValue(+model.BranchID);
      }
      if (model.SchedulerID) {
        this.f.scheduler.setValue(+model.SchedulerID);
      }
      if (model.SchedulerResourceID) {
        this.f.room.setValue(+model.SchedulerResourceID);
      }

      if (model.PriceListRefs) {
        this.priceListCodes = model.PriceListRefs;
        this.f.reason.setValue(this.priceListCodes);
      }

      if (model.Treatments) {
        this.treatments = model.Treatments;
        this.f.reason.setValue(this.treatments);
      }


      this.f.sunday.setValue(model.WeekDays[0]);
      this.f.monday.setValue(model.WeekDays[1]);
      this.f.tuesday.setValue(model.WeekDays[2]);
      this.f.wednesday.setValue(model.WeekDays[3]);
      this.f.thursday.setValue(model.WeekDays[4]);
      this.f.friday.setValue(model.WeekDays[5]);
      this.f.saturday.setValue(model.WeekDays[6]);
      this.f.duration.setValue(model.Duration);


      if (model.Note) {
        this.f.note.setValue(model.Note);
      }

      this.f.priority.setValue(model.Priority);

      if (model.FromTime && model.ToTime) {
        this.f.startTime.setValue(model.FromTime);
        this.f.endTime.setValue(model.ToTime);
      }

      if (model.FromDate && model.ToDate) {
        this.f.startDate.setValue(model.FromDate);
        this.f.endDate.setValue(model.ToDate);
      }

      this.fromTimeList = Utils.generateTimeList(model.Min, model.Max, 30, false);
      this.toTimeList = Utils.generateTimeList(model.Min, model.Max, 30, true);
    }
  }

  onChange() {
    this.subscribeChanges(); // using subscribe/unsubscribe to controll unwanted onChange triggering
  }

  subscribeChanges() {

    this.validateDropDowns(this.f.practice.value, this.f.branch.value, this.f.scheduler.value);

    this.subPractice = this.f.practice.valueChanges.subscribe(x => {
      console.log('practice change: ' + x);
      this.onPracticeChange(x);
    });

    this.subBranch = this.f.branch.valueChanges.subscribe(x => {
      console.log('branch change: ' + x);
      this.onBranchChange(x);
    });

    this.subScheduler = this.f.scheduler.valueChanges.subscribe(x => {
      console.log('scheduler change: ' + x);
      this.onSchedulerChange(x);
    });

    this.subRoom = this.f.room.valueChanges.subscribe(x => {
      console.log('room change: ' + x);
      this.onRoomChange(x);
    });

  }

  unsubscribeChanges() {
    if (this.subPractice) {
      this.subPractice.unsubscribe();
    }
    if (this.subBranch) {
      this.subBranch.unsubscribe();
    }
    if (this.subScheduler) {
      this.subScheduler.unsubscribe();
    }
    if (this.subRoom) {
      this.subRoom.unsubscribe();
    }
  }

  onSchedulerChange(scheduler: any) {
    this.unsubscribeChanges();

    // {code here} currently isnt used

    this.subscribeChanges();
  }

  onRoomChange(room: any) {
    this.unsubscribeChanges();

    // {code here} currently isnt used

    this.subscribeChanges();
  }

  onPracticeChange(practiceID) {
    this.unsubscribeChanges();

    const branchID = this.f.branch.value || 0;
    this.getBranches(practiceID);
    this.getScheduler(practiceID, branchID);
    // reset values
    this.f.branch.setValue(null);
    this.f.scheduler.setValue(null);
    this.f.room.setValue(null);
    this.f.reason.setValue(null);

    this.subscribeChanges();
  }

  onBranchChange(branch: any) {
    this.unsubscribeChanges();

    const branchID = this.f.branch.value || 0;
    const practiceID = this.f.practice.value || 0;

    this.f.room.setValue(null);
    this.getScheduler(practiceID, branchID);
    this.getRooms();
    this.f.scheduler.setValue(null);
    this.f.room.setValue(null);

    this.subscribeChanges();
  }

  getBranches(practiceID?: number) {
    this.standbyService.getBranches(practiceID).subscribe(result => {
      if (result.Code === 1) {
        // success
        this.branches = result.Data as ComplexBranch[];
        // reset branch, therapist, room , visit reason
      } else {
        this.error = this.translate.instant(`ERROR.CODE.${result.Code}`);
      }
    },
      error => {
        this.error = error;
      });

  }

  getScheduler(practiceID?: number, branchID?: number) {
    practiceID = this.f.practice.value || 0;
    branchID = this.f.branch.value || 0;

    this.standbyService.getSchedulers(practiceID, branchID).subscribe(result => {
      if (result.Code === 1) {
        // success
        this.schedulers = result.Data.map(x => {
          const item = new SelectListItem();
          item.Text = x.Name;
          item.Value = x.ID;
          return item;
        });
      } else {
        this.error = this.translate.instant(`ERROR.CODE.${result.Code}`);
      }
    },
      error => {
        this.error = error;
      });
  }

  getRooms() {
    const practiceID = this.f.practice.value || 0;
    const branchID = this.f.branch.value || 0;
    const scheduler = this.f.scheduler.value || null;
    const fromTime = this.f.startTime.value || null;
    const toTime = this.f.endTime.value || null;
    const sunday = this.f.sunday.value || false;
    const monday = this.f.monday.value || false;
    const tuesday = this.f.tuesday.value || false;
    const wednesday = this.f.wednesday.value || false;
    const thursday = this.f.thursday.value || false;
    const friday = this.f.friday.value || false;
    const saturday = this.f.saturday.value || false;

    this.standbyService.getRooms(
      practiceID, branchID, scheduler,
      fromTime, toTime, sunday, monday, tuesday,
      wednesday, thursday, friday, saturday)

      .subscribe(result => {
        if (result.Code === 1) {
          // success
          this.rooms = result.Data as SelectListItem[];
        } else {
          this.error = this.translate.instant(`ERROR.CODE.${result.Code}`);
        }
      },
        error => {
          this.error = error;
        });
  }

  canWatchConfidential(item: CustomerSearchResult) {
    if (this.hasPermission) {
      return true;
    } else {
      return !item.IsConfidential;
    }
  }


  /** create object to filter  */
  getFilterData(text): SearchCustomerFilter {
    return {
      FilterText: text,
      FilterPatients: true,
      FilterSuppliers: false,
      FilterPhoneBook: false,
      FilterActive: true,
      FilterInactive: false
    };
  }

  search(name: string) {
    this.searchText.next(name);
  }

  createDayOfWeekFormGroupItem(): UntypedFormGroup {
    return this.fb.group({
      id: 0,
      name: '',
      checked: false
    });
  }

  addDayOfWeek(): void {
    const items = this.form.get('daysOfWeek') as UntypedFormArray;
    items.push(this.createDayOfWeekFormGroupItem());
  }


  // convenience getter for easy access to form fields
  get f() { return this.form.controls; }

  // for debug only
  get diagnostic() { return JSON.stringify(this.form.value); }

  public onSubmit(values: StandByFormValues): void {
    if (this.form.valid) {
      // must select one from practice, branch or therpist/doctor
      if (!this.f.practice.value && !this.f.branch.value && !this.f.scheduler.value) {
        return;
      }

      values = this.form.value;
     
      const dataToSend: StandByModelToSend = {
        SchedulerWaitListID: values.waitlistID || 0,
        CustomerID: values.patient.ID,
        BranchID: values.branch,
        UserPractiseID: values.practice,
        // PriceListRefID: values.reason,
        PriceListRefID: 0,
        SchedulerID: values.scheduler,
        SchedulerResourceID: values.room,
        FromDate: values.startDate,
        ToDate: values.endDate,
        FromTime: values.startTime,
        ToTime: values.endTime,
        Duration: values.duration,
        Priority: values.priority,
        Order: 0,
        WeekDays: [
          values.sunday || false,
          values.monday || false,
          values.tuesday || false,
          values.wednesday || false,
          values.thursday || false,
          values.friday || false,
          values.saturday || false
        ],
        Note: values.note,
        Active: values.active,
        EventID: values.eventID,
        SchedulerTempWaitListID: 0,
        Treatments: this.treatments,  // covers treatmetns from patient card
        TreatmentsFromPriceList: JSON.stringify(this.priceListCodes) // covers treatmetns from  pricelist
      };


      this.standbyService.createStandBy(dataToSend).subscribe(result => {
        if (result.Code === 1) {
          // nullify saved data in appData and navigate to scheduler
          this.appData.priceListCodes = null;
          this.appData.treatments = null;
          this.appData.arguments = null;
          this.appData.formStateFindTime = null;

          this.router.navigate(['/waitlist']);
        } else {
          this.error = this.translate.instant(`ERROR.CODE.${result.Code}`);
        }
      },
        error => this.error = error);
    }
  }

  onCancel() {
    this.appData.formStateFindTime = null;
    this.appData.priceListCodes = null;
    this.appData.treatments = null;
    this.router.navigate(['/scheduler']);
  }

  updateResults(results: CustomerSearchResult[]): void {
    this.results = results;
    if (this.results.length === 0) {
      this.message = this.translate.instant('SEARCH.NOT-FOUND');
    } else {
      this.message = this.translate.instant('SEARCH.FOUND-X-PATIENTS', { num: this.results.length });
    }
  }

  displayFn(patient?: CustomerSearchResult): string | undefined {
    return patient ? patient.Name : undefined;
  }

  getTreatmentSummary(treatment: TreatmentEntity) {
    return Utils.treatmentToString(treatment, this.translate);
  }

  getPriceListSummary(priceListCode: PriceListCode) {
    return Utils.priceListCodeToString(priceListCode, this.translate);
  }

  // check for at least one dropdown selection,
  // if none are selected:  trigger validation warning for all of them.
  // if at least one is selected: clear validation warning from all of them.
  validateDropDowns(practice: number, branch: number, scheduler: number) {
    if (practice || branch || scheduler) {
      this.atLeastOneReqDropDownSelected = true;
      this.clearValidationFromRequiredDropDowns();
    } else {
      this.atLeastOneReqDropDownSelected = false;
      this.triggerValidationWarningForRequiredDropDowns();
    }
  }

  // clears the validation warning under the dropdowns and remove "required"
  clearValidationFromRequiredDropDowns() {
    this.f.practice.clearValidators();
    this.f.practice.updateValueAndValidity();
    this.f.branch.clearValidators();
    this.f.branch.updateValueAndValidity();
    this.f.scheduler.clearValidators();
    this.f.scheduler.updateValueAndValidity();
  }

  // set dropdowns as "required" and trigger the validation warning manually
  triggerValidationWarningForRequiredDropDowns() {
    this.f.practice.setValidators(Validators.required);
    this.f.branch.setValidators(Validators.required);
    this.f.scheduler.setValidators(Validators.required);
    this.f.practice.updateValueAndValidity();
    this.f.branch.updateValueAndValidity();
    this.f.scheduler.updateValueAndValidity();
    this.f.practice.markAsTouched();
    this.f.branch.markAsTouched();
    this.f.scheduler.markAsTouched();
    this.f.practice.markAsDirty();
    this.f.branch.markAsDirty();
    this.f.scheduler.markAsDirty();
  }

}


